import { downscaleImage } from '../image';
import type { AppState, Actions } from '../state';

export function renderCapture(root: HTMLElement, state: AppState, actions: Actions): void {
  const s = state.settings;
  const isNocturne = s.deliveryMode === 'nocturne-tray';
  const keyMissing = !s.apiKey;
  const noMachines = s.machines.every((m) => m.name.length === 0);
  const noBooks = s.books.every((b) => b.length === 0);
  const needsNocturneConfig = isNocturne && (noMachines || noBooks);
  const setupIncomplete = keyMissing || needsNocturneConfig;

  root.innerHTML = `
    <div class="screen screen-capture">
      <header class="topbar">
        <h1>Nocturne Handwriter</h1>
        <button class="btn btn-ghost" data-action="settings" aria-label="Settings">⚙</button>
      </header>

      ${setupIncomplete ? renderSetupBanner(keyMissing, isNocturne && noMachines, isNocturne && noBooks) : ''}

      ${!setupIncomplete && isNocturne ? renderMachinePicker(state) : ''}

      <div class="capture-area">
        ${state.isTranscribing
          ? `<div class="loading"><div class="spinner"></div><p>Transcribing…</p></div>`
          : renderCaptureControls(setupIncomplete, state)}
        ${state.errorMessage
          ? `<div class="error-banner">${esc(state.errorMessage)}</div>`
          : ''}
      </div>
    </div>
  `;

  root.querySelector<HTMLButtonElement>('[data-action="settings"]')!.addEventListener('click', () => {
    actions.go('settings');
  });

  const machineSelect = root.querySelector<HTMLSelectElement>('[data-field="machine"]');
  machineSelect?.addEventListener('change', () => {
    actions.setSelectedMachine(machineSelect.value);
  });

  const fileInput = root.querySelector<HTMLInputElement>('[data-action="capture"]');
  fileInput?.addEventListener('change', () => {
    const file = fileInput.files?.[0];
    if (file) void handleFile(file, actions);
  });

  const retryBtn = root.querySelector<HTMLButtonElement>('[data-action="retry"]');
  retryBtn?.addEventListener('click', () => {
    if (state.pendingImage) void actions.startTranscription(state.pendingImage);
  });
}

function renderSetupBanner(keyMissing: boolean, noMachines: boolean, noBooks: boolean): string {
  const missing: string[] = [];
  if (keyMissing) missing.push('a Gemini API key');
  if (noMachines) missing.push('at least one machine');
  if (noBooks) missing.push('at least one book');
  return `
    <div class="setup-banner">
      <p><strong>Setup required:</strong> configure ${missing.join(', ')} in Settings before capturing.</p>
    </div>
  `;
}

function renderMachinePicker(state: AppState): string {
  const named = state.settings.machines.filter((m) => m.name.length > 0);
  return `
    <label class="field">
      <span>Send to</span>
      <select data-field="machine">
        ${named
          .map(
            (m) =>
              `<option value="${esc(m.name)}"${m.name === state.selectedMachine ? ' selected' : ''}>${esc(m.name)}</option>`,
          )
          .join('')}
      </select>
    </label>
  `;
}

function renderCaptureControls(setupIncomplete: boolean, state: AppState): string {
  const disabled = setupIncomplete ? 'disabled' : '';
  const canRetry = !!state.pendingImage && !!state.errorMessage;
  const mainLabel = canRetry ? 'Pick a different photo' : 'Capture note';
  return `
    <label class="capture-button ${disabled ? 'is-disabled' : ''}">
      <input type="file" accept="image/*" data-action="capture" ${disabled} hidden />
      <span class="capture-button-icon">📷</span>
      <span class="capture-button-label">${mainLabel}</span>
    </label>
    <p class="capture-hint">Choose camera or photo library when prompted.</p>
    ${canRetry
      ? `<button class="btn btn-primary btn-block" data-action="retry" style="margin-top: 16px;">Try again with the same photo</button>`
      : ''}
  `;
}

async function handleFile(file: File, actions: Actions): Promise<void> {
  const prepared = await downscaleImage(file);
  await actions.startTranscription(prepared);
}

function esc(value: string): string {
  return value.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}
